import Phaser from "phaser";
import { formatClock } from "../constants";
import { addAudioToggles } from "./chrome";
import { hudStyle } from "./theme";
import type { RunSnapshot } from "../logic/runState";

const METER_NEAR_PROGRESS = 0.66;
const METER_WIDTH = 412;

export class Hud {
  private timeText: Phaser.GameObjects.Text;
  private scoreText: Phaser.GameObjects.Text;
  private paws: Phaser.GameObjects.Image[];
  private lastLives = 3;
  private lastClockSeconds = Number.NaN;
  private lastScore = Number.NaN;
  private lastMeterBucket = Number.NaN;
  private lastMeterKey = "cave-far";
  private meterFill: Phaser.GameObjects.Rectangle;
  private meterIcon: Phaser.GameObjects.Image;
  private clockRing: Phaser.GameObjects.Graphics;
  private readonly scene: Phaser.Scene;
  private readonly fadeTargets: Array<{ alpha: number }> = [];

  constructor(scene: Phaser.Scene, onPause: () => void) {
    this.scene = scene;
    const livesBg = scene.add.nineslice(118, 44, "panel-small", undefined, 188, 64, 36, 36, 28, 28);
    livesBg.setDepth(2000).setAlpha(0.96);
    this.paws = [0, 1, 2].map((index) => {
      const paw = scene.add.image(58 + index * 50, 44, "icon-life-bear-paw");
      paw.setDisplaySize(40, 46).setDepth(2001);
      return paw;
    });

    const timeBg = scene.add.nineslice(640, 44, "panel-small", undefined, 200, 64, 36, 36, 28, 28);
    timeBg.setDepth(2000).setAlpha(0.96);
    this.clockRing = scene.add.graphics().setDepth(2001);
    this.clockRing.setPosition(562, 44);
    const clock = scene.add.image(562, 44, "icon-time");
    clock.setDisplaySize(28, 28).setDepth(2002);
    this.timeText = scene.add.text(662, 44, "1:00", hudStyle(28));
    this.timeText.setOrigin(0.5).setDepth(2001);

    const scoreBg = scene.add.nineslice(1124, 44, "panel-small", undefined, 220, 64, 36, 36, 28, 28);
    scoreBg.setDepth(2000).setAlpha(0.96);
    const food = scene.add.image(1040, 44, "icon-score-food");
    food.setDisplaySize(36, 36).setDepth(2001);
    this.scoreText = scene.add.text(1070, 44, "0", hudStyle(26));
    this.scoreText.setOrigin(0, 0.5).setDepth(2001);

    const meterBg = scene.add.rectangle(640, 92, 420, 18, 0x2a1a10, 0.72).setDepth(2000);
    meterBg.setStrokeStyle(2, 0xf3e6c8, 0.55);
    this.meterFill = scene.add.rectangle(430, 92, 4, 12, 0xe8b84a).setOrigin(0, 0.5).setDepth(2001);
    this.meterIcon = scene.add.image(850, 92, "cave-far");
    this.meterIcon.setDisplaySize(34, 28).setDepth(2002);

    const pause = scene.add.image(48, 108, "icon-pause");
    pause.setDisplaySize(40, 40).setDepth(2001).setInteractive({ useHandCursor: true });
    pause.on("pointerup", onPause);

    const toggles = addAudioToggles(scene, 48, 160, 36, 2001);
    this.fadeTargets.push(
      livesBg,
      ...this.paws,
      timeBg,
      this.clockRing,
      clock,
      this.timeText,
      scoreBg,
      food,
      this.scoreText,
      meterBg,
      this.meterFill,
      this.meterIcon,
      pause,
      toggles.music,
      toggles.sfx,
      toggles.fullscreen,
    );
  }

  fadeOut(duration: number): void {
    this.scene.tweens.add({
      targets: this.fadeTargets,
      alpha: 0,
      duration,
      ease: "Sine.easeIn",
    });
  }

  refresh(snapshot: RunSnapshot): void {
    const clockSeconds = Math.max(0, Math.ceil(snapshot.remainingMs / 1000));
    if (clockSeconds !== this.lastClockSeconds) {
      this.lastClockSeconds = clockSeconds;
      this.timeText.setText(formatClock(snapshot.remainingMs));
      this.drawClockRing(clockSeconds / 60);
    }

    if (snapshot.score !== this.lastScore) {
      this.lastScore = snapshot.score;
      this.scoreText.setText(String(snapshot.score));
      if (this.scoreText.width > 150) {
        this.scoreText.setFontSize(22);
      }
    }

    if (snapshot.lives !== this.lastLives) {
      this.paws.forEach((paw, index) => {
        const alive = index < snapshot.lives;
        paw.setAlpha(alive ? 1 : 0.22);
        paw.setTint(alive ? 0xffffff : 0x3a3a3a);
      });
      if (snapshot.lives < this.lastLives) {
        const lost = this.paws[snapshot.lives];
        if (lost) {
          this.playLifePulse(lost.x, lost.y);
        }
      }
      this.lastLives = snapshot.lives;
    }

    const meterBucket = Math.max(4, Math.round(METER_WIDTH * snapshot.progress));
    if (meterBucket !== this.lastMeterBucket) {
      this.lastMeterBucket = meterBucket;
      this.meterFill.width = meterBucket;
    }

    const meterKey =
      snapshot.progress > METER_NEAR_PROGRESS && this.scene.textures.exists("cave-entrance")
        ? "cave-entrance"
        : this.scene.textures.exists("cave-far")
          ? "cave-far"
          : this.lastMeterKey;
    if (meterKey && meterKey !== this.lastMeterKey) {
      this.lastMeterKey = meterKey;
      this.meterIcon.setTexture(meterKey);
    }
  }

  private drawClockRing(portion: number): void {
    this.clockRing.clear();
    this.clockRing.lineStyle(5, 0x2a1a10, 0.9);
    this.clockRing.strokeCircle(0, 0, 18);
    this.clockRing.lineStyle(4, 0xe8b84a, 1);
    this.clockRing.beginPath();
    this.clockRing.arc(0, 0, 18, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.max(0, portion), false);
    this.clockRing.strokePath();
  }

  private playLifePulse(x: number, y: number): void {
    if (!this.scene.anims.exists("life-lost-pulse")) {
      return;
    }
    const fx = this.scene.add.sprite(x, y, "life-lost-pulse", 0);
    fx.setDepth(2010).setScale(0.28).play("life-lost-pulse");
    fx.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => fx.destroy());
  }
}
